import { useMemo, useRef, type CSSProperties } from 'react';

import { useGetBiomassDailySummary } from '@restDataAccess';
import { FlexAligner } from '@scaleaq/scaleui';
import { InventoryProvider, MappingCollections } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { BiomassModal, BiomassWidgetConfig, useBiomassChart } from '@/components/biomassWidget/';
import { BiomassContent } from '@/components/biomassWidget/BiomassContent.tsx';
import { Content } from '@/components/cardWithThreeFooterBox';
import { AggregatedHighChart, ChartAggregationResolution } from '@/components/chart';
import { GridWidget } from '@/components/GridWidget.tsx';
import { useDimensions, useLayoutProps, useOpenModal } from '@/hooks';
import { widthPxToGridColumns } from '@/pages/landingPage/grid/widgetConstraints.ts';
import { ComponentType, GenericWidgetProps } from '@/types.ts';
import { getRefetchJitterMs, Icon, responsive } from '@/utils';
import { BIOMASS_COMPONENT_NAME } from '@/widgetComponentNames.ts';

import { getTwoColumnWidgetLayout } from '../utils/twoColumnWidgetLayout/getTwoColumnWidgetLayout.ts';
import { BIOMASS_TWO_COLUMN_WIDGET_LAYOUT } from './biomassWidget/biomassTwoColumnWidgetLayout.ts';

const defaultSize = { w: 18, h: 9 };
const BIOMASS_SUMMARY_DAYS = 7;
const BIOMASS_SUMMARY_REFETCH_MS = 60 * 60 * 1000;
const biomassNormalizeTimeSeries = [
  MappingCollections.inventoryProviderClassPair[InventoryProvider.Biomass],
];

interface BiomassWidgetProps extends GenericWidgetProps {
  config?: BiomassWidgetConfig;
  editable?: boolean;
  widgetId: string;
  style?: CSSProperties;
  isStandalone?: boolean;
}

export const BiomassWidget: ComponentType<BiomassWidgetProps> = ({
  config: configInput,
  editable = true,
  widgetId,
  style,
  isStandalone = false,
  ...rest
}: BiomassWidgetProps) => {
  const { t } = useTranslation(['common', 'orbitOne']);

  const modalController = useOpenModal();

  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useDimensions(containerRef, 100);

  const { config, size = defaultSize } = useLayoutProps<BiomassWidgetConfig>({
    configInput,
    defaultConfig: { id: '', name: '' },
    widgetId,
  });

  // Width stays 0 until the container is measured. Treating that as columns collapses the chart.
  const effectiveColumns = isStandalone && width > 0 ? widthPxToGridColumns(width) : size.w;

  const { shouldFirstChildRender, shouldSecondChildRender, firstChildFlex, secondChildFlex } =
    getTwoColumnWidgetLayout({
      ...BIOMASS_TWO_COLUMN_WIDGET_LAYOUT,
      numberOfColumnsInWidget: effectiveColumns,
    });

  // Add a random 1–20s jitter once per mount so summaries across pens don't refetch in lockstep.
  const summaryRefetchMs = useMemo(() => BIOMASS_SUMMARY_REFETCH_MS + getRefetchJitterMs(), []);

  const { data, isLoading, isError, isFetched, isRateLimited } = useGetBiomassDailySummary({
    days: BIOMASS_SUMMARY_DAYS,
    refetchInterval: summaryRefetchMs,
    inventoryId: config.id,
  });

  const hasSensor = !!config.id;
  const totalObservationCount = data?.totalFishCount ?? 0;
  const hasFish = totalObservationCount > 0;

  const { editId } = useMemo(() => ({ editId: uuidv4() }), []);

  const toolbarActionList = useMemo(
    () =>
      editable
        ? [
            {
              id: editId,
              label: t('edit'),
              onClick: modalController.handleOpenModal,
              Icon: Icon.SlidersHorizontal,
            },
          ]
        : [],
    [editId, editable, modalController.handleOpenModal, t]
  );

  const { chartConfig, chartDataStat, setChartDataStat } = useBiomassChart(config.id);

  const headerSubtitle = useMemo(() => {
    if (!hasSensor || isLoading || !isFetched) {
      return undefined;
    }
    return t('orbitOne:biomass.subTitle', {
      context: hasFish ? 'withFish' : 'noFish',
      numberOfObservations: totalObservationCount,
      days: BIOMASS_SUMMARY_DAYS,
    });
  }, [hasFish, hasSensor, isFetched, isLoading, t, totalObservationCount]);

  return (
    <FlexAligner fullHeight ref={containerRef}>
      <GridWidget
        {...rest}
        headerSubtitle={headerSubtitle}
        headerTitle={t('orbitOne:biomass.title', { sensorName: ` (${config.name})` })}
        id={widgetId}
        isLoading={chartDataStat.isLoading}
        isRateLimited={isRateLimited || chartDataStat.isRateLimited}
        cloudBacked={true}
        style={{ ...style }}
        toolbarActionList={toolbarActionList}
        {...{ ...chartDataStat, isEmpty: undefined }}
      >
        <FlexAligner
          direction={'row'}
          style={{
            flex: 1,
            minHeight: 0,
            alignSelf: 'stretch',
            padding: `${responsive.spacing['xlarge']} ${responsive.spacing['xlarge']} 0`,
          }}
        >
          {shouldFirstChildRender && (
            <Content $flex={firstChildFlex} style={{ height: 'fit-content' }}>
              <BiomassContent
                data={data}
                hasSensor={hasSensor}
                isError={isError}
                isFetched={isFetched}
                isLoading={isLoading}
              />
            </Content>
          )}
          {shouldSecondChildRender && (
            <Content $flex={secondChildFlex} style={{ minHeight: 0, overflow: 'hidden' }}>
              <AggregatedHighChart
                config={chartConfig}
                setChartDataStat={setChartDataStat}
                normalizeTimeSeries={
                  chartConfig.resolution === ChartAggregationResolution.DAILY
                    ? biomassNormalizeTimeSeries
                    : undefined
                }
              />
            </Content>
          )}
        </FlexAligner>
      </GridWidget>
      <BiomassModal widgetId={widgetId} modalController={modalController} />
    </FlexAligner>
  );
};

BiomassWidget.componentName = BIOMASS_COMPONENT_NAME;
